/**
 * frontend/src/utils/httpErrors.ts
 * REFACTOR-SPLIT (PLAT-UTILS-1 R1): catalogs moved to ./httpErrors.generated.ts
 *
 * HttpErrorCatalog — single source of truth for human-readable HTTP error
 * translation in the QoE React frontend.
 *
 * The catalogs (HTTP_STATUS_CATALOG, APP_ERROR_CATALOG) are generated from
 * core/exceptions.py :: HTTP_STATUS_DESCRIPTIONS + APP_ERROR_DESCRIPTIONS — see
 * ./httpErrors.generated.ts. Never hand-edit a catalog entry here; add the
 * code in core/exceptions.py and run `python scripts/generate_error_catalog.py`.
 *
 * Usage:
 *   import { HttpErrorCatalog } from '@/utils/httpErrors'
 *
 *   // In an axios error interceptor or catch block:
 *   const msg = HttpErrorCatalog.format(error.response?.status, errorBody?.error?.code)
 *   // → "401 - Unauthorized — authentication is required — please log in
 *   //    | AUTHENTICATION_REQUIRED — authentication is required — please log in to continue"
 *
 *   // Status code only:
 *   HttpErrorCatalog.translateStatus(405)
 *   // → "405 - Method Not Allowed — this endpoint does not accept this HTTP method"
 *
 *   // App error code only:
 *   HttpErrorCatalog.translateCode('DEAL_NOT_FOUND')
 *   // → "DEAL_NOT_FOUND — the requested deal does not exist"
 */

// ---------------------------------------------------------------------------
// HTTP status + app error catalogs — generated, see ./httpErrors.generated.ts
// ---------------------------------------------------------------------------

import { HTTP_STATUS_CATALOG, APP_ERROR_CATALOG } from './httpErrors.generated'

// ---------------------------------------------------------------------------
// Network-level error catalog (client-side: no HTTP response received)
// ---------------------------------------------------------------------------

const NETWORK_ERROR_CATALOG: Record<string, string> = {
  ERR_NETWORK:                'could not connect to the server',
  ERR_NETWORK_CHANGED:        'network configuration changed mid-request',
  ERR_TIMED_OUT:              'connection timed out',
  ERR_ABORTED:                'request was aborted',
  ERR_NAME_NOT_RESOLVED:      'server hostname could not be resolved',
  ERR_INTERNET_DISCONNECTED:  'no internet connection',
  ERR_CERT_AUTHORITY_INVALID: 'server certificate is not trusted',
  ECONNREFUSED:               'server refused the connection',
  ECONNRESET:                 'connection was reset by the server',
  ETIMEDOUT:                  'connection timed out',
}

// ---------------------------------------------------------------------------
// HttpErrorCatalog — the public API
// ---------------------------------------------------------------------------

export const HttpErrorCatalog = {
  /**
   * Translate an HTTP status code to a human-readable string.
   *
   * translateStatus(401)
   * → "401 - Unauthorized — authentication is required — please log in"
   */
  translateStatus(status: number): string {
    const entry = HTTP_STATUS_CATALOG[status]
    if (entry) return `${status} - ${entry.name} — ${entry.description}`
    return `${status} - HTTP ${status}`
  },

  /**
   * Translate an application error code to a human-readable string.
   *
   * translateCode('DEAL_NOT_FOUND')
   * → "DEAL_NOT_FOUND — the requested deal does not exist"
   */
  translateCode(code: string): string {
    const desc = APP_ERROR_CATALOG[code]
    return desc ? `${code} — ${desc}` : `${code} — unrecognized error code`
  },

  /**
   * Format a full error message combining HTTP status, app error code, and
   * any backend-provided message detail.
   *
   * format(401, 'AUTHENTICATION_REQUIRED', 'Missing Bearer token')
   * → "401 - Unauthorized — authentication is required — please log in
   *    | AUTHENTICATION_REQUIRED — authentication is required — please log in to continue
   *    | Missing Bearer token"
   */
  format(status?: number, code?: string, backendMessage?: string): string {
    const parts: string[] = []
    if (status != null) parts.push(this.translateStatus(status))
    if (code && code !== 'UNKNOWN_ERROR') parts.push(this.translateCode(code))
    if (backendMessage) parts.push(backendMessage)
    return parts.join(' | ') || 'An unknown error occurred'
  },

  /**
   * Extract status + code from an AxiosError (HTTP error — response received).
   * Safe to call with any thrown value.
   */
  fromAxiosError(err: unknown): string {
    if (!err || typeof err !== 'object') return 'An unknown error occurred'
    const e = err as {
      response?: {
        status?: number
        data?: { detail?: unknown; error?: { code?: string; message?: string } }
      }
    }
    const status = e.response?.status
    const code = e.response?.data?.error?.code
    const message = e.response?.data?.error?.message

    // VALIDATION-1C: the backend speaks in two shapes. A YansarException lands
    // as {error: {code, message}} — handled below, and the catalog can say more
    // about it because it has a code. A plain FastAPI HTTPException lands as
    // {detail: "..."}, which this function never read, so every written
    // rejection a router raised ("This file is 41.2 MB — the limit is 25 MB…")
    // reached the user as "413 - HTTP 413" and the real message was discarded.
    //
    // Where a detail string exists it IS the user-facing copy — routers in this
    // codebase write it for exactly that purpose. Return it alone: prefixing
    // the status would put the bare code back in front of the user, which is
    // the defect. A non-string detail is FastAPI's own validation-error array,
    // which is machine output, so that falls through to the catalog.
    if (!code && !message) {
      const detail = e.response?.data?.detail
      if (typeof detail === 'string' && detail.trim()) return detail.trim()
    }
    return this.format(status, code, message)
  },

  /**
   * Translate a network-level error (no HTTP response — request never completed).
   * Covers: axios ERR_NETWORK, browser AbortError (timeout), fetch TypeError (offline/DNS).
   */
  fromNetworkError(err: unknown): string {
    if (!err || typeof err !== 'object') return `Network error: ${String(err)}`
    const e = err as { name?: string; message?: string; code?: string }

    // Browser AbortError — fetch signal timeout or manual abort
    if (e.name === 'AbortError') {
      return 'Network error: Request timed out — the server took too long to respond'
    }

    // Browser fetch TypeError — DNS failure, offline, CORS pre-flight rejection
    if (e.name === 'TypeError' && e.message?.toLowerCase().includes('fetch')) {
      return 'Network error: Failed to reach the server — check your connection or try again'
    }

    // Axios ERR_NETWORK / node.js POSIX codes
    const code = e.code ?? 'ERR_NETWORK'
    const desc = NETWORK_ERROR_CATALOG[code] ?? 'the request did not complete'
    return `Network error (${code}): ${desc}${e.message ? ` — ${e.message}` : ''}`
  },

  /**
   * Universal catch-all — pass ANYTHING from a catch block.
   *
   * Detection order:
   *  1. Axios HTTP error (has .response)   → fromAxiosError
   *  2. Axios/fetch network error (no response, or AbortError / TypeError)
   *                                         → fromNetworkError
   *  3. Any other Error with .message       → message text
   *  4. Unknown                             → fallback string
   *
   * This is the method you should call in every catch block.
   */
  fromAnyError(err: unknown): string {
    if (err == null) return 'An unknown error occurred'
    if (typeof err !== 'object') return String(err)

    const e = err as {
      isAxiosError?: boolean
      response?: { status?: number }
      name?: string
      message?: string
      code?: string
    }

    // Axios HTTP error
    if (e.isAxiosError && e.response) return this.fromAxiosError(err)

    // Axios network error (no response) or browser fetch errors
    if (
      (e.isAxiosError && !e.response) ||
      e.name === 'AbortError' ||
      (e.name === 'TypeError' && e.message?.toLowerCase().includes('fetch'))
    ) {
      return this.fromNetworkError(err)
    }

    // Plain Error or any other thrown object
    return e.message || 'An unknown error occurred'
  },
} as const

// ---------------------------------------------------------------------------
// Convenience re-exports for callers that prefer a functional style
// ---------------------------------------------------------------------------

export const translateStatus  = (status: number) => HttpErrorCatalog.translateStatus(status)
export const translateCode    = (code: string)   => HttpErrorCatalog.translateCode(code)
export const formatApiError   = (err: unknown)   => HttpErrorCatalog.fromAnyError(err)
